import { LitElement, html, type PropertyValues } from 'lit';
import {
  ALL_TEMPLATE_VARIABLES,
  isKnownTemplateVariable,
  isSenderTemplateVariable,
  SENDER_CHIP_THEME,
  VARIABLE_CHIP_THEME,
} from './types.js';

const VAR_REGEX = /\{\{(\w+)\}\}/g;

const CHIP_STYLE_BASE = [
  'display:inline-block',
  'padding:1px 8px',
  'margin:0 1px',
  'border-radius:6px',
  'font-size:0.85em',
  'line-height:1.6',
  'font-weight:500',
  'vertical-align:baseline',
  'user-select:none',
  'cursor:default',
];

const CHIP_STYLE = [
  ...CHIP_STYLE_BASE,
  `background:${VARIABLE_CHIP_THEME.bg}`,
  `color:${VARIABLE_CHIP_THEME.color}`,
  `border:${VARIABLE_CHIP_THEME.border}`,
].join(';');

// Sender tokens ({{sender_*}}) get their own hue, so the rep-derived variables
// read as a distinct group from the lead ones at a glance.
const CHIP_STYLE_SENDER = [
  ...CHIP_STYLE_BASE,
  `background:${SENDER_CHIP_THEME.bg}`,
  `color:${SENDER_CHIP_THEME.color}`,
  `border:${SENDER_CHIP_THEME.border}`,
].join(';');

// Distinct styling for a {{token}} that ISN'T one of the documented
// TEMPLATE_VARIABLES — e.g. a typo. The backend still resolves it
// case/underscore-insensitively against the real lead fields when possible,
// but if it genuinely doesn't match anything it silently renders a generic
// fallback ("there", "your company", ...) — worth flagging visually rather
// than styling it identically to a confirmed-working variable.
const CHIP_STYLE_UNRECOGNIZED = [
  ...CHIP_STYLE_BASE,
  'background:oklch(0.75 0.15 60 / 0.15)',
  'color:oklch(0.6 0.15 60)',
  'border:1px dashed oklch(0.6 0.15 60 / 0.6)',
].join(';');

function labelForKey(key: string): string {
  return ALL_TEMPLATE_VARIABLES.find(v => v.key === key)?.label ?? key;
}

/** Whether `key` matches one of the documented variables (lead or sender). */
const isRecognizedVariable = (key: string): boolean => isKnownTemplateVariable(key);

export class PromptEditorElement extends LitElement {
  // Use light DOM so Tailwind / DaisyUI utility classes work
  override createRenderRoot() {
    return this;
  }

  static override properties = {
    value: { type: String },
    placeholder: { type: String },
    rows: { type: Number },
  };

  value = '';
  placeholder = '';
  rows = 5;

  private _editor: HTMLDivElement | null = null;
  private _internalValue = '';

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  override render() {
    const minH = Math.max(this.rows * 1.5, 2.5);
    // Use this.value for placeholder check (not _internalValue which syncs later)
    const showPlaceholder = !this.value;
    return html`
      <div style="position:relative; width:100%;">
        <div
          class="prompt-editor-ce textarea textarea-bordered w-full"
          contenteditable="true"
          spellcheck="true"
          style="min-height:${minH}rem; white-space:pre-wrap; word-break:break-word; overflow-y:auto;"
          @input=${this._handleInput}
          @drop=${this._handleDrop}
          @dragover=${(e: DragEvent) => e.preventDefault()}
          @paste=${this._handlePaste}
        ></div>
        ${showPlaceholder
          ? html`<div
              class="prompt-editor-placeholder"
              style="position:absolute; top:0; left:0; right:0; padding:0.5rem 0.75rem; pointer-events:none; color:oklch(var(--bc) / 0.3); font-size:0.875rem; white-space:pre-wrap;"
              >${this.placeholder}</div
            >`
          : ''}
      </div>
    `;
  }

  override firstUpdated() {
    this._editor = this.querySelector('.prompt-editor-ce');
    this._internalValue = this.value;
    this._syncToDOM();
  }

  override updated(changed: PropertyValues) {
    if (!this._editor) this._editor = this.querySelector('.prompt-editor-ce');
    if (changed.has('value') && this.value !== this._internalValue) {
      this._internalValue = this.value;
      this._syncToDOM();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Insert a variable chip at the current cursor position. */
  insertVariable(key: string) {
    const editor = this._editor;
    if (!editor) return;
    editor.focus();

    const chip = this._createChip(key);
    const sel = window.getSelection();

    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (editor.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(chip);
        const after = document.createRange();
        after.setStartAfter(chip);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
      } else {
        editor.appendChild(chip);
      }
    } else {
      editor.appendChild(chip);
    }

    this._readAndEmit();
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  private _createChip(key: string): HTMLSpanElement {
    const span = document.createElement('span');
    span.contentEditable = 'false';
    span.dataset.variable = key;
    const recognized = isRecognizedVariable(key);
    const style = !recognized ? CHIP_STYLE_UNRECOGNIZED : isSenderTemplateVariable(key) ? CHIP_STYLE_SENDER : CHIP_STYLE;
    span.setAttribute('style', style);
    span.textContent = labelForKey(key);
    if (!recognized) {
      span.title = `"${key}" isn't one of the standard variables — it'll still try to match a lead field, but falls back to a generic filler if nothing matches. Check the variable list below.`;
    }
    return span;
  }

  /** Rebuild the contenteditable DOM from the plain-text value. */
  private _syncToDOM() {
    const editor = this._editor;
    if (!editor) return;
    editor.innerHTML = '';

    const text = this._internalValue;
    if (!text) return;

    let lastIdx = 0;
    const regex = new RegExp(VAR_REGEX.source, 'g');
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIdx) {
        this._appendText(editor, text.slice(lastIdx, match.index));
      }
      editor.appendChild(this._createChip(match[1]!));
      lastIdx = match.index + match[0].length;
    }

    if (lastIdx < text.length) {
      this._appendText(editor, text.slice(lastIdx));
    }
  }

  private _appendText(parent: HTMLElement, text: string) {
    const lines = text.split('\n');
    lines.forEach((line, idx) => {
      if (idx > 0) parent.appendChild(document.createElement('br'));
      if (line) parent.appendChild(document.createTextNode(line));
    });
  }

  /** Walk the contenteditable DOM and reconstruct the plain-text value. */
  private _readFromDOM(): string {
    const editor = this._editor;
    if (!editor) return '';
    return this._extractText(editor);
  }

  private _extractText(node: Node): string {
    let result = '';
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        result += child.textContent ?? '';
      } else if (child instanceof HTMLElement) {
        if (child.dataset.variable) {
          result += `{{${child.dataset.variable}}}`;
        } else if (child.tagName === 'BR') {
          result += '\n';
        } else if (child.tagName === 'DIV' || child.tagName === 'P') {
          // Browsers wrap lines in <div>/<p> when pressing Enter
          if (result.length > 0 && !result.endsWith('\n')) result += '\n';
          result += this._extractText(child);
        } else {
          result += this._extractText(child);
        }
      }
    }
    return result;
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  private _handleInput() {
    this._readAndEmit();
  }

  private _handlePaste(e: ClipboardEvent) {
    // Paste as plain text, converting {{var}} tokens to chips
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const frag = document.createDocumentFragment();
    const regex = new RegExp(VAR_REGEX.source, 'g');
    let lastIdx = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIdx) {
        this._appendTextToFragment(frag, text.slice(lastIdx, match.index));
      }
      frag.appendChild(this._createChip(match[1]!));
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < text.length) {
      this._appendTextToFragment(frag, text.slice(lastIdx));
    }

    range.insertNode(frag);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);

    this._readAndEmit();
  }

  private _appendTextToFragment(frag: DocumentFragment, text: string) {
    const lines = text.split('\n');
    lines.forEach((line, idx) => {
      if (idx > 0) frag.appendChild(document.createElement('br'));
      if (line) frag.appendChild(document.createTextNode(line));
    });
  }

  private _handleDrop(e: DragEvent) {
    const text = e.dataTransfer?.getData('text/plain') ?? '';
    const match = text.match(/^\{\{(\w+)\}\}$/);
    if (!match) return; // let browser handle non-variable drops

    e.preventDefault();
    const editor = this._editor;
    if (!editor) return;

    const chip = this._createChip(match[1]!);

    // Insert at drop position using caretRangeFromPoint
    if (document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range && editor.contains(range.startContainer)) {
        range.insertNode(chip);
      } else {
        editor.appendChild(chip);
      }
    } else {
      editor.appendChild(chip);
    }

    this._readAndEmit();
  }

  private _readAndEmit() {
    this._internalValue = this._readFromDOM();
    this.value = this._internalValue; // keep property in sync for placeholder visibility
    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        detail: this._internalValue,
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define('prompt-editor', PromptEditorElement);

declare global {
  interface HTMLElementTagNameMap {
    'prompt-editor': PromptEditorElement;
  }
}
